/**
 * Shared formatting utilities
 * Used across the web app for consistent formatting
 */

/**
 * Format cents to a dollar string (e.g., 1234 -> "$12.34")
 */
export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Format a date string to MM/dd/yyyy format (matching iOS)
 * Tries multiple input formats and returns formatted date or original string
 */
export function formatDate(dateString: string): string {
  // Try to parse yyyy-MM-dd format (ISO)
  const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${month}/${day}/${year}`;
  }

  // If already in MM/dd/yyyy format, return as-is
  const usMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (usMatch) {
    return dateString;
  }

  // Return original if can't parse
  return dateString;
}

/**
 * Receipt monospace font styling - uses SF Mono on Apple devices, falls back to similar fonts
 * This matches iOS's .monospaced design more closely than Courier New
 */
export const receiptFont = {
  fontFamily: '"SF Mono", "Menlo", "Monaco", "Consolas", "Liberation Mono", "Courier New", monospace',
};

/**
 * System font styling - matches iOS SF Pro
 */
export const systemFont = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

/**
 * Format a fraction label (e.g., 2 -> "1/2", 1 -> "Whole")
 */
export function fractionLabel(denominator: number): string {
  if (denominator === 1) return 'Whole';
  return `1/${denominator}`;
}
